import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, effect, input, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellType, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { QuillViewComponent } from 'ngx-quill'
import { Subscription } from 'rxjs'
import { ContentChangeNotification, MeetingEntityTypes } from 'src/app/shared/services/meeting-hub.service'
import { getInitials } from 'src/app/shared/shared-functions'

import {
    BaseComponent,
    CompanyQuarter,
    FeatureType,
    Grid,
    ListItem,
    Meeting,
    ModalBackDrop,
    ModalSize,
    Rock,
    RockMilestone,
    User,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { MeetingItemsSelectionComponent } from '../meeting-items-selection/meeting-items-selection.component'
import { RockMilestoneComponent } from './milestone/rock-milestone.component'
import { RockComponent } from './rock.component'

@Component({
    selector: 'app-rocks',
    templateUrl: './rocks.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        QuillViewComponent,
    ],
})
export class RocksComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch

    meetingId = input.required<string>()
    meetings = input.required<Meeting[]>()
    meeting = input.required<Meeting>()
    isActiveMeeting = input(false)

    rockId = signal('')
    showArchived = signal(false)

    allUsers: User[] = []
    allUserMap!: DataMap
    allActiveUsers: User[] = []
    allActiveUserList: ListItem<string>[] = []

    statusList: ListItem<number>[] = [
        new ListItem<number>(0, 'Off Track'),
        new ListItem<number>(1, 'On Track'),
        new ListItem<number>(2, 'Done'),
        new ListItem<number>(3, 'Cancelled'),
    ]
    statusMap = new DataMap(this.statusList, 'value', 'display')

    typeList: ListItem<number>[] = [new ListItem<number>(0, 'Individual'), new ListItem<number>(1, 'Company')]
    typeMap = new DataMap(this.typeList, 'value', 'display')

    quarter: CompanyQuarter | null = null

    canViewAdmin = true

    grid: Grid<Rock>
    milestonesGrid: Grid<RockMilestone>

    private contentSyncSub?: Subscription
    private isCellEditing = false
    private pendingContentSync: ContentChangeNotification | null = null

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.MeetingsMeetingView])
    }

    constructor() {
        super()

        effect(() => {
            if (this.meetingId()) {
                this.refresh()
                this.refreshRocks()
            }
        })

        this.grid = new Grid<Rock>(this.services)
            .setupModalEditing(this.services.data.rocks, 'ROCKS', RockComponent, () => new Rock(), this, ModalSize.large)
            .filterBy(item => [item.name])
            .isReadOnly(false)

        this.grid.itemAdded.on(this.handleRockAdded.bind(this))
        this.grid.selectionChanged.on(this.onRockSelectionChanged)

        this.milestonesGrid = new Grid<RockMilestone>(this.services)
            .setupModalEditing(
                this.services.data.rockMilestones,
                'Milestone',
                RockMilestoneComponent,
                () => new RockMilestone(),
                this,
                ModalSize.medium,
            )
            .filterBy(item => [item.name])
            .isReadOnly(false)

        this.milestonesGrid.itemAdded.on(this.handleMilestoneAdded.bind(this))

        this.contentSyncSub = this.services.meetingContentSync.subscribe({
            meetingId: () => this.meetingId(),
            entityTypes: [MeetingEntityTypes.Rock, MeetingEntityTypes.RockMilestone],
            isEditing: () => this.isCellEditing,
            onRefresh: n => this.handleRemoteContentChange(n),
            onDeferredRefresh: n => {
                this.pendingContentSync = n
            },
        })
    }

    ngOnDestroy(): void {
        this.contentSyncSub?.unsubscribe()
        this.grid.itemAdded.off(this.handleRockAdded.bind(this))
        this.grid.selectionChanged.off(this.onRockSelectionChanged)
        this.milestonesGrid.itemAdded.off(this.handleMilestoneAdded.bind(this))
    }

    private onRockSelectionChanged = (_rock: Rock) => {
        this.refreshMilestones()
    }

    handleRockAdded() {
        // The user could have added multiple users, so we need to reload the Rocks to ensure the extra Rocks are loaded
        this.refreshRocks()
    }

    handleMilestoneAdded() {
        // The user could have added multiple users, so we need to reload the Milestones to ensure the extra Milestones are loaded
        this.refreshMilestones()
    }

    private handleRemoteContentChange(notification: ContentChangeNotification): void {
        if (notification.entityType === MeetingEntityTypes.Rock) {
            this.refreshRocks(true)
            return
        }
        if (notification.entityType === MeetingEntityTypes.RockMilestone) {
            const selectedRockId = this.rockId()
            if (
                !notification.parentEntityId ||
                !selectedRockId ||
                notification.parentEntityId.toLowerCase() === selectedRockId.toLowerCase()
            ) {
                this.refreshMilestones(true)
            }
        }
    }

    beginningEdit(): void {
        this.isCellEditing = true
    }

    refresh() {
        this.cancelableRequest(
            'RocksRefresh',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.users.queryAllByAction('GetForLookup'),
                    this.services.data.get('/api/CompanyQuarter/GetCurrentQuarter'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.allUsers = result[0]
                        this.allUserMap = new DataMap(this.allUsers, 'id', 'displayName')

                        this.allActiveUsers = this.allUsers.filter(user => user.active)
                        this.allActiveUserList = this.allActiveUsers.map(user => new ListItem(user.id, user.displayName))

                        this.quarter = result[1]
                    },
                }),
        )
    }

    refreshRocks(silent = false) {
        const params = new HttpParams()
            .set('meetingId', this.meetingId())
            .set('showArchived', this.showArchived().toString())
        this.cancelableRequest(
            'RefreshRocks',
            !silent,
            this.services.data.load(!silent, !silent, this.services.data.rocks.queryAllByAction('GetByMeetingId', params)).subscribe({
                next: result => {
                    this.grid.items = result[0]
                },
            }),
        )
    }

    refreshMilestones(silent = false) {
        const rock = this.grid.selectedItem as Rock
        if (rock) {
            this.rockId.set(rock.id)
            const params = new HttpParams().set('rockId', rock.id)
            this.cancelableRequest(
                '',
                !silent,
                this.services.data.load(!silent, !silent, this.services.data.rockMilestones.queryAllByAction('GetByRockId', params)).subscribe({
                    next: result => {
                        this.milestonesGrid.items = result[0]
                    },
                }),
            )
        }
    }

    gridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c] as any
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as Rock | RockMilestone

            // Reset cell class and style due to cell recycling
            cell.classList.remove('yellow-cell')
            cell.style.textDecoration = 'unset'

            if (
                col.binding === 'doneDate' ||
                col.binding === 'status' ||
                col.binding === 'dueDate' ||
                col.binding === 'name' ||
                col.binding === 'type'
            )
                cell.classList.add('yellow-cell')

            if (dataItem && dataItem.doneDate != null) {
                cell.style.textDecoration = 'line-through'
            }

            if (col.binding === 'userId') {
                const originalVal = cell.innerHTML
                cell.title = originalVal
                cell.innerHTML = getInitials(originalVal)
            }
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) {
            this.isCellEditing = false
            return
        }
        this.updateRecords([flexGrid.rows[args.row].dataItem])
        this.isCellEditing = false
        if (this.pendingContentSync) {
            const pending = this.pendingContentSync
            this.pendingContentSync = null
            this.handleRemoteContentChange(pending)
        }
    }

    updateRecords(items: any[]) {
        const item = items[0]

        if (item.status === 2 && item.doneDate === null) {
            item.doneDate = new Date()
        }

        if (item instanceof Rock) {
            this.services.data.rocks.edit(item).subscribe({
                next: () => {
                    this.grid.refresh()
                },
                error: () => {
                    this.services.notification.error('The rock changes were not successfully saved')
                },
            })
        } else if (item instanceof RockMilestone) {
            this.services.data.rockMilestones.edit(item).subscribe({
                next: () => {
                    this.milestonesGrid.refresh()
                },
                error: () => {
                    this.services.notification.error('The milestone changes were not successfully saved')
                },
            })
        }
    }

    showSelectionModal() {
        const ref = this.services.modals.open(MeetingItemsSelectionComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as MeetingItemsSelectionComponent
        instance.itemType = 'Rocks'
        instance.meetingId = this.meetingId()
        instance.itemIds = this.grid.items.map(x => x.id)
        ref.result.then(
            () => {
                this.refreshRocks()
            },
            () => {
                console.log('dismissed')
            },
        )
    }

    archiveRock() {
        const rock = this.grid.selectedItem as Rock
        if (!rock) {
            this.services.notification.warning('Please select a ROCK to archive')
            return
        }

        if (rock.isArchived) {
            this.services.notification.warning('This ROCK is already archived')
            return
        }

        rock.isArchived = true
        this.services.data.rocks.edit(rock).subscribe({
            next: () => {
                this.services.notification.success('ROCK archived successfully')
                this.refreshRocks()
            },
            error: error => {
                this.services.notification.error('Failed to archive ROCK: ' + error.message)
            },
        })
    }

    toggleShowArchived() {
        this.showArchived.set(!this.showArchived())
        this.refreshRocks()
    }
}
